import { FC, useCallback, useEffect } from 'react'

import { FormTextarea, resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'

import { ESP_TRANSLATION_KEYS } from '../../../configs/translations'
import { ESP_REJECT_FORM_KEY } from '../../../constants'

import styles from './EspRejectDialog.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.admin
const FORM_KEY = ESP_REJECT_FORM_KEY

interface IEspRejectFormData {
  reason: string
}

interface IEspRejectDialogContentProps {
  isSubmitting: boolean
  onSubmit: (reason: string) => void
  onCancel: () => void
}

const INITIAL_FORM: IEspRejectFormData = { reason: '' }

const EspRejectDialogContent = ({
  isSubmitting,
  onSubmit,
  onCancel,
}: IEspRejectDialogContentProps) => {
  const dispatch = useAppDispatch()
  const [{ formData }] = useGetFormData<IEspRejectFormData>(FORM_KEY, INITIAL_FORM)

  const handleSubmit = useCallback(async () => {
    const validation = FormValidationBuilder.build(FORM_KEY)
    const errors = await Promise.all([validation.call('reason', formData.reason)])

    if (errors.some((error): error is TFormValidationResult => Boolean(error))) {
      return
    }

    onSubmit(formData.reason)
  }, [formData.reason, onSubmit])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(FORM_KEY))
    }
  }, [dispatch])

  return (
    <div className={styles.root}>
      <FormTextarea
        isRequired
        formKey={FORM_KEY}
        field="reason"
        defaultValue={INITIAL_FORM.reason}
        label={KEYS.rejectReasonLabel}
        rows={3}
      />

      <div className={styles.footer}>
        <Button variant="outlined" color="grey" onClick={onCancel} disabled={isSubmitting}>
          <Translation text={KEYS.rejectCancel} />
        </Button>

        <Button
          color="danger"
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          <Translation text={KEYS.rejectSubmit} />
        </Button>
      </div>
    </div>
  )
}

interface IEspRejectDialogProps {
  show: boolean
  isSubmitting: boolean
  onSubmit: (reason: string) => void
  onCancel: () => void
}

/** Captures the required rejection reason (FR-10: reject always carries a reason). */
export const EspRejectDialog: FC<IEspRejectDialogProps> = ({
  show,
  isSubmitting,
  onSubmit,
  onCancel,
}) => (
  <ModalDialog<IEspRejectDialogContentProps>
    show={show}
    onClose={onCancel}
    size="sm"
    header={() => (
      <div className={styles.title}>
        <Translation text={KEYS.rejectDialogTitle} span />
      </div>
    )}
    content={EspRejectDialogContent}
    contentProps={{ isSubmitting, onSubmit, onCancel }}
    closeOnOutsideClick={false}
  />
)

EspRejectDialog.displayName = 'EspRejectDialog'
